/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link, useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import { Divider, Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import MDBox from "~/components/MDBox";
import MDTypography from "~/components/MDTypography";
import { setClickedCardIndex } from "~/store/slices/utilities";

function Breadcrumbs({ icon, title, route, light }) {
  const routes = route.slice(0, -1);
  const location = useLocation();
  const dispatch = useDispatch();
  // Path that you don't want to include in details breadcrumbs
  const excludedPath = [
    "/plantation-master/",
    "/category-master",
    "/plant-master",
    "/location-master",
    "/plotting-master",
    "/plantation-master",
  ];
  const currentName = location.state?.name;

  useEffect(() => {
    // This will reset the ClickCard Index routes apart from excluded array
    if (!["dashboard", "plots", "plants", "plantation"].includes(route[0]))
      dispatch(
        setClickedCardIndex({
          route: "",
          index: "",
        })
      );
  }, []);

  // Memoize the previous names array using useMemo
  const previousNames = useMemo(() => {
    const storedNames =
      JSON.parse(sessionStorage.getItem("previousNames")) || [];
    if (
      location.pathname === "/dashboard" ||
      (excludedPath.includes(location.pathname) && location.state !== null)
    ) {
      storedNames.length = 0;
      sessionStorage.setItem("previousNames", JSON.stringify(storedNames));
      return []; // Don't add any data if on the dashboard route
    }
    // Get the previous names from sessionStorage or any other storage mechanism

    // Check if the current name is not already in the stored names array
    if (!storedNames.includes(currentName)) {
      // Add the current name to the stored names
      // storedNames.push(currentName);
      storedNames.push(currentName);
      // Save the updated names array to sessionStorage for persistence
      sessionStorage.setItem("previousNames", JSON.stringify(storedNames));
    }

    // Return the updated names array
    return storedNames;
  }, [title]);

  return (
    <MDBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: ({ palette: { white, grey } }) =>
              light ? white.main : grey[600],
          },
        }}
      >
        <Link to="/">
          <MDTypography
            component="span"
            variant="body2"
            color={light ? "white" : "dark"}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0 }}
          >
            <Icon>{icon}</Icon>
          </MDTypography>
        </Link>
        {routes.map((el, index) => (
          <MDBox key={el}>
            {el === "dashboard" ? (
              <>
                <MDTypography
                  component="span"
                  variant="button"
                  fontWeight="regular"
                  textTransform="capitalize"
                  color={light ? "white" : "dark"}
                  opacity={light ? 0.8 : 0.5}
                  sx={{ lineHeight: 0 }}
                >
                  {el}
                </MDTypography>
              </>
            ) : null}
            {el !== "dashboard" ? (
              <MDBox
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <MDTypography
                  component="span"
                  variant="button"
                  fontWeight="regular"
                  textTransform="capitalize"
                  color={light ? "white" : "dark"}
                  opacity={light ? 0.8 : 0.5}
                  sx={{ lineHeight: 0 }}
                >
                  {el}
                </MDTypography>
                {!excludedPath.includes(location.pathname) &&
                location.state?.name ? (
                  <>
                    <Divider sx={{ marginTop: 0.4, marginBottom: 0.4 }} />
                    <MDTypography variant="caption" textTransform="capitalize">
                      {previousNames[index - 1]}
                    </MDTypography>
                  </>
                ) : null}
              </MDBox>
            ) : null}
            {/* <MDTypography
                component="span"
                variant="button"
                fontWeight="regular"
                textTransform="capitalize"
                color={light ? 'white' : 'dark'}
                opacity={light ? 0.8 : 0.5}
                sx={{ lineHeight: 0 }}
                gutterBottom
              >
                {el}
              </MDTypography> */}
          </MDBox>
        ))}
        <MDTypography
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          color={light ? "white" : "dark"}
          sx={{ lineHeight: 0 }}
        >
          {title.replace(/%20|-/g, " ")}
        </MDTypography>
      </MuiBreadcrumbs>
      {/*? This was a title next to the breadcrumbs */}
      {/* <MDTypography
        fontWeight="bold"
        textTransform="capitalize"
        variant="h6"
        color={light ? 'white' : 'dark'}
        noWrap
      >
        {title.replace(/%20|-/g, ' ')}
      </MDTypography> */}
    </MDBox>
  );
}

// Setting default values for the props of Breadcrumbs
Breadcrumbs.defaultProps = {
  light: false,
};

// Typechecking props for the Breadcrumbs
Breadcrumbs.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  light: PropTypes.bool,
};

export default Breadcrumbs;
